import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../utils/useTranslation';
import { db } from '../../firebase';
import { getDocs, collection, query, where } from 'firebase/firestore';
import HeroBackground from '../../components/ui/HeroBackground';
import Pagination from '../../components/Pagination';
import { motion } from 'framer-motion';
import ajrakTexture from '../../assets/ajrak_texture_new.jpg';

const ITEMS_PER_PAGE = 9;

export default function BranchesPage() {
  const t = useT();
  const isRTL = t.language === 'sindhi';

  const [branches, setBranches] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [branchSnap, districtSnap] = await Promise.all([
          getDocs(collection(db, 'branches')),
          getDocs(collection(db, 'districts')),
        ]);

        const activeBranches = branchSnap.docs
          .map(d => ({ _id: d.id, ...d.data() }))
          .filter(b => b.status === 'active');
        
        setBranches(activeBranches);
        setDistricts(districtSnap.docs.map(d => ({ _id: d.id, ...d.data() })));

      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const filtered = branches.filter(b => {
    if (!b) return false; // Guard against bad data in the array
    const s = search.toLowerCase();
    
    // Safely get name and city, falling back to an empty string to prevent crashes
    const name = (isRTL ? (b.nameSindhi || b.name) : b.name) || '';
    const city = (isRTL ? (b.cityVillageSindhi || b.cityVillage || b.city) : (b.cityVillage || b.city)) || '';
    
    return name.toLowerCase().includes(s) || city.toLowerCase().includes(s);
  });

  // Fetch secretary names only for visible branches to optimize performance
  useEffect(() => {
    const fetchSecretaries = async () => {
      const visibleBranches = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
      const secretaryIds = [...new Set(visibleBranches.map(b => b.secretaryId).filter(id => id && !members.find(m => m._id === id)))];
      
      if (secretaryIds.length > 0) {
        try {
          // Firebase 'in' query supports up to 30 items
          const chunks = [];
          for (let i = 0; i < secretaryIds.length; i += 30) {
            chunks.push(secretaryIds.slice(i, i + 30));
          }

          const results = await Promise.all(chunks.map(chunk => 
            getDocs(query(collection(db, 'members'), where('__name__', 'in', chunk)))
          ));

          const newMembers = results.flatMap(snap => snap.docs.map(d => ({ _id: d.id, ...d.data() })));
          if (newMembers.length > 0) {
            setMembers(prev => [...prev, ...newMembers]);
          }
        } catch (err) {
          console.error("Error fetching secretaries:", err);
        }
      }
    };

    if (!loading) fetchSecretaries();
  }, [page, filtered, loading]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const renderBranchCard = (b) => {
    if (!b) return null; // Defensively return nothing if a bad record somehow gets here

    const bName = (isRTL ? (b.nameSindhi || b.name) : b.name) || 'Unnamed Branch';
    const bCity = (isRTL ? (b.cityVillageSindhi || b.cityVillage || b.city) : (b.cityVillage || b.city)) || '';
    const dist = districts.find(d => d._id === b.districtId);
    const bDist = dist ? (isRTL ? (dist.nameSindhi || dist.name) : dist.name) : '—';
    const secretary = members.find(m => m._id === b.secretaryId);
    const location = [bCity, bDist].filter(Boolean).join(', ');

    return (
      <Card key={b._id} style={{ overflow: 'hidden', background: 'white', textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg, var(--sindhi-maroon), var(--sindhi-gold))' }} />
        <div style={{ padding: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <h3 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 24 : 20, fontWeight: 800, color: 'var(--sindhi-navy)' }}>{bName}</h3>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: 'var(--sindhi-ivory)', color: 'var(--sindhi-maroon)', padding: '4px 10px', borderRadius: 8, letterSpacing: 0.5 }}>{t('active')}</span>
          </div>
          
          {location && <div style={{ fontSize: 15, color: 'var(--sindhi-text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-start', flexDirection: isRTL ? 'row-reverse' : 'row', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            <span>📍</span> {location}
          </div>}

          {secretary && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
               <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', opacity: 0.6, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('secretary')}</span>
                <span style={{ fontWeight: 700, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                    {(isRTL ? secretary.full_name_sindhi : secretary.full_name) || secretary.full_name}
                </span>
              </div>
            </div>
          )}
          
          <Link to={`/branch/${b._id}`} className="btn btn-primary btn-sm" style={{ marginTop: 24, width: '100%', justifyContent: 'center', padding: 12, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {t('viewBranch')}
          </Link>
        </div>
      </Card>
    );
  };

  return (
    <>
      <div className="hero-premium">
        <HeroBackground />
        <div style={{ position: 'relative', zIndex: 10, direction: isRTL ? 'rtl' : 'ltr' }}>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 56 : 48, fontWeight: 800, color: 'var(--sindhi-gold)', marginBottom: 16, letterSpacing: -1, textAlign: isRTL ? 'right' : 'left' }}>
            {t('ourBranches')}
          </motion.h1>
          <p style={{ opacity: 0.8, fontSize: 18, maxWidth: 600, margin: isRTL ? '0 0 0 auto' : '0 auto 0 0', fontWeight: 500, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', textAlign: isRTL ? 'right' : 'left' }}>
            {t('branchesSubtitle')}
          </p>
          <div style={{ marginTop: 40, maxWidth: 500, margin: isRTL ? '40px 0 0 auto' : '40px auto 0 0' }}>
            <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 20, top: '50%', transform: 'translateY(-50%)', opacity: 0.6, fontSize: 20 }}>🔍</span>
                <input className="form-input" placeholder={t('searchBranch')} value={search} onChange={e => setSearch(e.target.value)} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', paddingLeft: isRTL ? 20 : 54, paddingRight: isRTL ? 54 : 20, height: 60, borderRadius: 16, fontSize: 16, backdropFilter: 'blur(20px)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', direction: isRTL ? 'rtl' : 'ltr' }} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--sindhi-ivory)', backgroundImage: `url(${ajrakTexture})`, backgroundSize: '600px 600px', backgroundRepeat: 'repeat', position: 'relative', zIndex: 10, marginTop: '-40px' }}>
          <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', background: 'rgba(253,248,235,0.7)', zIndex: 0, pointerEvents: 'none' }} />
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '80px 24px 100px', position: 'relative', zIndex: 1 }}>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
            ) : filtered.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: '80px 0', color: 'var(--sindhi-text-muted)' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>🍃</div>
                    {t('noBranches')}
                </Card>
            ) : (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24, direction: isRTL ? 'rtl' : 'ltr' }}>
                        {filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map(renderBranchCard)}
                    </div>
                    {!loading && filtered.length > ITEMS_PER_PAGE && (
                        <div style={{ marginTop: 48, display: 'flex', justifyContent: 'center', direction: 'ltr' }}>
                        <Pagination page={page} totalPages={Math.ceil(filtered.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
                        </div>
                    )}
                </>
            )}
        </div>
      </div>
    </>
  );
}
